// Anexo do chat do Dwight (HydraDrawer): o que entra, até que tamanho, e como a
// tela descreve o envio. Lógica pura — o componente só chama.
//
// 13/10/2026: PDF passou de 25 para 50 MB (pedido do Lucas). Os outros tipos
// ficam em 25 MB: imagem vai inteira para o modelo e arquivo de mapa é lido de
// uma vez no servidor — nenhum dos dois foi medido acima disso. O que acontece
// com o PDF grande do lado do servidor: docs/dwight-escrita-segura.md.

const MB = 1024 * 1024;
export const LIMITE_PDF_MB = 50;
export const LIMITE_OUTROS_MB = 25;
export const DICA_ANEXOS = `PDF até ${LIMITE_PDF_MB}MB · KMZ/KML/SHP/imagens até ${LIMITE_OUTROS_MB}MB`;

type Arquivo = { name: string; type?: string; size: number };
type AnexoEnviado = { nome: string; tipo?: string; tamanho: number };

export function ehPdf(nome: string, tipo?: string): boolean {
  return (tipo || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(nome || '');
}

export function tipoAceito(nome: string, tipo?: string): boolean {
  return /\.(pdf|kml|kmz|zip|geojson|json|png|jpe?g|gif|webp)$/i.test(nome || '')
    || (tipo || '').startsWith('image/') || (tipo || '') === 'application/pdf';
}

export function limiteMb(nome: string, tipo?: string): number {
  return ehPdf(nome, tipo) ? LIMITE_PDF_MB : LIMITE_OUTROS_MB;
}

// "48 MB", "3,2 MB", "512 KB" — do jeito que a pessoa lê no Explorer.
export function tamanhoLegivel(bytes: number): string {
  const b = Math.max(0, Number(bytes) || 0);
  if (b < MB) return `${Math.max(1, Math.round(b / 1024))} KB`;
  const mb = b / MB;
  return `${mb < 10 ? mb.toFixed(1).replace('.', ',') : Math.round(mb)} MB`;
}

// Por que o arquivo NÃO entra — ou null se entra.
export function recusaDoAnexo(f: Arquivo): string | null {
  if (!tipoAceito(f.name, f.type)) {
    return `Arquivo "${f.name}" não é aceito. Tipos: PDF, KML/KMZ, Shapefile (.zip), GeoJSON e imagens.`;
  }
  const limite = limiteMb(f.name, f.type);
  if (f.size > limite * MB) {
    const regra = ehPdf(f.name, f.type) ? `PDF entra até ${LIMITE_PDF_MB} MB` : `KMZ, shapefile e imagem entram até ${LIMITE_OUTROS_MB} MB`;
    return `Arquivo "${f.name}" muito grande (${tamanhoLegivel(f.size)}). ${regra}.`;
  }
  return null;
}

// Nome com que o arquivo sobe ao S3. O leitor de PDF do servidor reconhece o
// arquivo pela terminação ".pdf" da chave: PDF sem extensão no nome subiria e
// nunca seria lido.
export function nomeParaUpload(nome: string, tipo?: string): string {
  return ehPdf(nome, tipo) && !/[.]pdf$/i.test(nome || '') ? `${nome || 'documento'}.pdf` : nome;
}

// Quanto o navegador espera o envio ao S3. Arquivo pequeno fica nos 2 min de
// antes; o grande ganha o tempo de uma conexão de 100 KB/s, até 10 min (50 MB
// ≈ 8min44s). Com os 2 min fixos, 50 MB só subia em conexão acima de 3,5 Mbps.
export function timeoutUploadMs(bytes: number): number {
  return Math.min(600_000, Math.max(120_000, Math.round((Number(bytes) || 0) / 100)));
}

// Texto da barra enquanto o arquivo sobe: 'Enviando "edital.pdf" · 42% de 48 MB'.
export function rotuloEnvio(nome: string, enviados: number, total: number): string {
  const t = Number(total) || 0;
  if (t <= 0) return `Enviando "${nome}"`;
  const pct = Math.min(100, Math.max(0, Math.floor(((Number(enviados) || 0) / t) * 100)));
  return `Enviando "${nome}" · ${pct}% de ${tamanhoLegivel(t)}`;
}

// PDF grande demora a ser lido (o escaneado passa por OCR, ~1,4 página por
// segundo): quem espera precisa saber que não travou.
export const PDF_DEMORADO_MB = 10;
export function avisoLeituraDemorada(anexos: AnexoEnviado[]): string | null {
  const grande = (anexos || []).some(a => ehPdf(a.nome, a.tipo) && a.tamanho > PDF_DEMORADO_MB * MB);
  return grande ? 'Lendo o PDF — arquivo grande ou escaneado pode levar alguns minutos.' : null;
}
